<template>
  <div class="p-6">
    <div class="mb-6">
      <h1 class="text-2xl font-bold text-gray-900 dark:text-white mb-2">
        Secciones
      </h1>
      <p class="text-gray-600 dark:text-gray-400">
        Catálogo de secciones
      </p>
    </div>
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
      <div class="p-6 border-b border-gray-200 dark:border-gray-700">
        <div class="flex justify-between items-center">
          <h2 class="text-lg font-semibold text-gray-900 dark:text-white">
            Lista de Secciones
          </h2>
          <button @click="showModal = true" class="bg-violet-600 hover:bg-violet-700 text-white px-4 py-2 rounded-lg transition-colors duration-200 flex items-center gap-2">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
            </svg>
            Nueva Sección
          </button>
        </div>
        <!-- Filtro -->
        <form @submit.prevent="onFilter" class="flex flex-wrap gap-2 mt-4">
          <input v-model="filters.nombre" type="text" placeholder="Nombre" class="px-3 py-2 rounded border text-sm bg-gray-50 dark:bg-gray-900 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-200" />
          <button type="submit" class="px-4 py-2 rounded bg-blue-500 text-white text-sm font-semibold hover:bg-blue-600 transition">Buscar</button>
          <button type="button" @click="onClearFilters" class="px-4 py-2 rounded bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-sm font-semibold hover:bg-gray-300 dark:hover:bg-gray-600 transition">Limpiar</button>
        </form>
      </div>
      <div class="p-6">
        <div v-if="error" class="text-red-500 text-center mb-4">{{ error }}</div>
        <div class="overflow-x-auto">
          <table class="w-full text-sm text-left text-gray-500 dark:text-gray-400">
            <thead class="text-xs uppercase bg-gray-100 dark:bg-gray-800 dark:text-gray-300 border-b-2 border-gray-200 dark:border-gray-700">
              <tr>
                <th class="px-4 py-2 font-bold">ID</th>
                <th class="px-4 py-2 font-bold">Nombre</th>
                <th class="px-4 py-2 font-bold">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="seccion in secciones" :key="seccion.id" class="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 even:bg-gray-50 dark:even:bg-gray-700">
                <td class="px-4 py-2 font-medium text-gray-900 dark:text-white">{{ seccion.id }}</td>
                <td class="px-4 py-2 text-gray-700 dark:text-gray-200">{{ seccion.nombre }}</td>
                <td class="px-4 py-2 whitespace-nowrap text-right">
                  <button @click="openEditModal(seccion)" class="text-blue-500 hover:text-blue-700 mr-2" title="Editar">
                    <svg class="w-5 h-5 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.232 5.232l3.536 3.536M9 13h3l8-8a2.828 2.828 0 00-4-4l-8 8v3z"/>
                    </svg>
                  </button>
                  <button @click="onDeleteSeccion(seccion)" class="text-red-500 hover:text-red-700" title="Eliminar">
                    <svg class="w-5 h-5 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                    </svg>
                  </button>
                </td>
              </tr>
              <tr v-if="secciones.length === 0">
                <td colspan="3" class="text-center py-4 text-gray-400">No hay secciones para mostrar.</td>
              </tr>
            </tbody>
          </table>
        </div>
        <!-- Paginador -->
        <Pagination 
          :current-page="currentPage" 
          :last-page="lastPage" 
          @page-changed="goToPage" 
        />
      </div>
    </div>
    <!-- Modal para Nueva/Editar Sección -->
    <div v-if="showModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div class="p-6 border-b border-gray-200 dark:border-gray-700">
          <div class="flex justify-between items-center">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
              {{ editMode ? 'Editar Sección' : 'Nueva Sección' }}
            </h3>
            <button @click="closeModal" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>
        <form @submit.prevent="editMode ? updateSeccion() : createSeccion()" class="p-6 space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nombre *</label>
            <input v-model="seccionForm.nombre" type="text" required class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 dark:bg-gray-700 dark:text-white" placeholder="Ingrese el nombre" />
          </div>
          <div class="flex justify-end space-x-3 pt-4">
            <button type="button" @click="closeModal" class="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors">Cancelar</button>
            <button type="submit" :disabled="loading" class="px-4 py-2 bg-violet-600 text-white rounded-md hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2">
              <svg v-if="loading" class="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
              </svg>
              {{ loading ? (editMode ? 'Actualizando...' : 'Creando...') : (editMode ? 'Actualizar' : 'Crear') }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watchEffect } from 'vue'
import { useHttp } from '@/composables/useHttp'
import Swal from 'sweetalert2'
import Pagination from '@/components/Pagination.vue'

const secciones = ref([])
const currentPage = ref(1)
const lastPage = ref(1)
const perPage = 12
const showModal = ref(false)
const editMode = ref(false)
const seccionForm = ref({ id: null, nombre: '' })
const filters = ref({ nombre: '' })
const { get, post, del, loading, error, data } = useHttp()

const fetchSecciones = async (page = 1) => {
  try {
    const params = new URLSearchParams()
    params.append('page', page)
    params.append('per_page', perPage)
    if (filters.value.nombre) params.append('nombre', filters.value.nombre)
    await get(`secciones?${params.toString()}`)
    if (data.value) {
      if (Array.isArray(data.value)) {
        secciones.value = data.value
        currentPage.value = 1
        lastPage.value = 1
      } else {
        secciones.value = data.value.data
        currentPage.value = data.value.current_page
        lastPage.value = data.value.last_page
      }
    }
  } catch (err) {
    secciones.value = []
  }
}

const goToPage = (page) => {
  if (page >= 1 && page <= lastPage.value) fetchSecciones(page)
}

const onFilter = () => { fetchSecciones(1) }
const onClearFilters = () => { filters.value = { nombre: '' }; fetchSecciones(1) }

const closeModal = () => {
  showModal.value = false
  editMode.value = false
  seccionForm.value = { id: null, nombre: '' }
}

const openEditModal = (seccion) => {
  seccionForm.value = { ...seccion }
  showModal.value = true
  editMode.value = true
}

const createSeccion = async () => {
  try {
    await post('secciones', { nombre: seccionForm.value.nombre })
    closeModal()
    await fetchSecciones(currentPage.value)
    Swal.fire({ icon: 'success', title: 'Sección creada', text: 'La sección se creó exitosamente.', timer: 2000, showConfirmButton: false })
  } catch (err) {
    Swal.fire({ icon: 'error', title: 'Error', text: error.value || 'Error desconocido al crear sección' })
  }
}

const updateSeccion = async () => {
  try {
    console.log("antes de put");
    await post(`secciones/${seccionForm.value.id}?_method=PUT`, { nombre: seccionForm.value.nombre })
    closeModal()
    console.log("antes de fetch");
    await fetchSecciones(currentPage.value)
    Swal.fire({ icon: 'success', title: 'Sección actualizada', text: 'La sección se actualizó exitosamente.', timer: 2000, showConfirmButton: false })
  } catch (err) {
    Swal.fire({ icon: 'error', title: 'Error', text: error.value || 'Error desconocido al actualizar sección' })
  }
}

const onDeleteSeccion = async (seccion) => {
  const result = await Swal.fire({
    title: `¿Eliminar sección?`,
    text: `¿Seguro que deseas eliminar "${seccion.nombre}"? Esta acción no se puede deshacer.`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#d33',
    cancelButtonColor: '#3085d6',
    confirmButtonText: 'Sí, eliminar',
    cancelButtonText: 'Cancelar',
  })
  if (result.isConfirmed) {
    try {
      await del(`secciones/${seccion.id}`)
      await Swal.fire({ title: '¡Eliminado!', text: 'La sección ha sido eliminada correctamente.', icon: 'success', timer: 1800, showConfirmButton: false })
      fetchSecciones(currentPage.value)
    } catch (err) {
      Swal.fire('Error', 'No se pudo eliminar la sección.', 'error')
    }
  }
}

watchEffect(() => { fetchSecciones(currentPage.value) })
</script>